import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild, SimpleChanges, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NanorepService } from '../nanorep-panel/nanorep.service';
import { NanorepAccountInfoModel } from 'common/api/models/nanorep-account-info.model';
import { WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { BcAuth } from 'common/auth/auth.service';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { NanorepInterface, SmartAdvisorWrapper } from 'shared/components/nanorep-panel/nanorep-sdk';
import { SmartAdvisorLoaderService } from 'shared/components/smart-advisor/smart-advisor-loader.service';
import { SmartAdvisorService } from 'shared/components/smart-advisor/smart-advisor.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ChatMessageGroupModel } from 'shared/components/chat-messages/chat-message-group.model';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { Chat } from 'common/xislivechat/models/chat';
import { Subscription } from 'rxjs';

interface WidgetEvents {
  suggestArticle({ name, title, content }: { name: string; title: string; content: string }): void;
  copyToChat(text: string): void;
  load(): void;
  newComment({ comment }: { comment: string }, articleId: string): void;
}

@Component({
  selector: 'smart-advisor',
  templateUrl: './smart-advisor.component.html',
  styleUrls: ['./smart-advisor.component.scss']
})

export class SmartAdvisorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpened: boolean;
  @Input() isExpanded: boolean;
  @Input() workItemId: string;
  @Input() workItemType: WorkItemTypeValue;
  @Input() nanorepConfigId: string;
  @Input() visitor: VisitorInfoModel;
  @Input() chat: Chat;
  @Input() chatIsEnded: boolean;
  @ViewChild('wrapper', { static: true }) wrapper: ElementRef;
  @ViewChild('mountNode', { static: true }) mountNode: ElementRef;
  @ViewChild('headNode', { static: true }) headNode: ElementRef;

  public workItemFocusedSubscription: Subscription;
  public accountInfo: NanorepAccountInfoModel;
  private widgetEvents: WidgetEvents;
  private _rxEmitterSubscription: Subscription;
  private widgetInstance: SmartAdvisorWrapper;

  constructor(
    private bcAuth: BcAuth,
    private rxEmitter: RxEmitter,
    private smartAdvisorService: SmartAdvisorService,
    private _nanorepService: NanorepService,
    private _scriptLoaderService: SmartAdvisorLoaderService,
    private _userPermissionService: UserPermissionService,
    private _workItemStateHandler: WorkItemStateHandler,
    private translateService: TranslateService
  ) {}

  async ngOnInit() {
    this.workItemFocusedSubscription = this._workItemStateHandler.workItemFocused$.subscribe(focusedWorkItemId => {
      if (focusedWorkItemId === this.workItemId) {
        this.revealWidget();
      } else if (this.widgetInstance && this.widgetEvents) {
        this.widgetInstance.whenAppReady().then(widget => {
          widget.off(this.widgetEvents);
          this.widgetEvents = null;
        });
      }
    });

    this.accountInfo = await this._nanorepService.getAccountInfo();
    this.subscribeToChatMessages();
    await this._scriptLoaderService.loadScript(this.accountInfo);

    const focusedWorkItem = this._workItemStateHandler.getFocusedWorkItem();
    if (focusedWorkItem.id === this.workItemId) {
      await this.revealWidget();
    }

    if (this.wrapper.nativeElement.offsetHeight) {
      this.smartAdvisorService.mountToNode(this.mountNode.nativeElement);
    }
  }

  async ngOnChanges({ chatIsEnded }: SimpleChanges) {
    if (!this.smartAdvisorService.instance) {
      return;
    }

    if (chatIsEnded) {
      if (chatIsEnded.currentValue) {
        await this.smartAdvisorService.wrapUp(this.chat.ChatID);
      }
    }

    this.updateHeight();
  }

  async ngOnDestroy() {
    const widget = await this.smartAdvisorService.instance.whenAppReady();
    widget.off(this.widgetEvents);
    this.widgetEvents = null;

    if (this.workItemFocusedSubscription) {
      this.workItemFocusedSubscription.unsubscribe();
      this.workItemFocusedSubscription = null;
    }

    if (this._rxEmitterSubscription) {
      this._rxEmitterSubscription.unsubscribe();
      this._rxEmitterSubscription = null;
    }
  }

  private subscribeToChatMessages() {
    if (!this._rxEmitterSubscription) {
      this._rxEmitterSubscription = this.rxEmitter.subscribe(LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.SendWorkItemContent, this.workItemId), (messages: ChatMessageGroupModel[]) => {
        const lastMessageGroup = messages[messages.length - 1];
        if (lastMessageGroup && lastMessageGroup.type === bcEnums.messageTypes.VISITOR) {
          const lastMessageGroupMessages = lastMessageGroup.messages;
          this.smartAdvisorService.ask(lastMessageGroupMessages.map((message) => message.Text).join(' '));
        }
      });
    }
  }

  private async subscribeToWidgetEvents() {
    if (this.widgetEvents) {
      return;
    }

    const load = () => {
      this.updateHeight();
    };

    const copyToChat = (text) => {
      this.smartAdvisorService.copySelected(text, this.workItemId);
    };

    const suggestArticle = ({ title, content }) => {
      this.smartAdvisorService.showSuggestionSentMessage();
      this._nanorepService.sendMessageToProposeAnArticle(this.chat.ChatID, content, title);
    };

    const newComment = ({ comment }, articleId) => {
      this._nanorepService.sendArticleComment(articleId, comment);
    };

    this.widgetEvents = { load, copyToChat, suggestArticle, newComment };
    const widget = await this.smartAdvisorService.instance.whenAppReady();
    widget.on(this.widgetEvents);
  }

  private canAgentProposeArticle(): boolean {
    return !!this.nanorepConfigId && this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_SUGGEST_ARTICLE);
  }

  private async revealWidget() {
    this.widgetInstance = await this.smartAdvisorService.createWidget({
      agentName: this.bcAuth.getSessionUserName(),
      suggestionsEnabled: this.canAgentProposeArticle(),
      currentLanguage: this.translateService.currentLang
    }, {
      host: this.accountInfo.nanorepHost,
      account: this.accountInfo.nanorepHost,
      forceConfigId: this.nanorepConfigId
    }, this.mountNode.nativeElement);
    await this.subscribeToWidgetEvents();
  }

  private updateHeight() {
    const wrapperElement = this.wrapper.nativeElement;
    const headElement = this.headNode.nativeElement;
    this.smartAdvisorService.updateScrollableHeight(wrapperElement.offsetHeight - headElement.offsetHeight);
  }
}

declare global {
  interface Window {
    nanorep: NanorepInterface;
  }
}
